import React, { useState, useEffect, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import Step1Personal from './Step1Personal';
import Step2Address from './Step2Address';
import Step3Work from './Step3Work';
import Step4Documents from './Step4Documents';
import { submitOnboarding } from '../../services/platformApi';
import './OnboardingWizard.css';
import logo from "../../assets/images/cto_logo.png";

// Documents (aadhar/pan/photo) are actual File objects — not JSON-serializable,
// and browsers won't let JS repopulate a <input type="file"> after a refresh
// anyway (security restriction). So only personal/address/work + the current
// step get backed up; documents always have to be re-picked after a reload.
const DRAFT_KEY = 'cto_onboarding_draft';

const loadDraft = () => {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const OnboardingWizard = ({ onComplete }) => {
  const { accounts } = useMsal();
  const draft = useRef(loadDraft()).current; // read once, before first render

  const [showWizard, setShowWizard] = useState(!!draft);
  const [currentStep, setCurrentStep] = useState(
    draft ? Math.min(Math.max(draft.currentStep || 1, 1), 3) : 1
    // capped at 3: if the user had reached step 4, documents can't survive a
    // refresh anyway, so drop them back to Work and let them move forward again
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [availableApps] = useState([]);

  const [formData, setFormData] = useState({
    personal: {
      company_email: '',
      personal_email: '',
      full_name: '',
      phone: '',
      date_of_birth: '',
      gender: '',
      blood_group: '',
      ...(draft?.personal || {}),
    },
    address: {
      current_address: '',
      current_city: '',
      current_state: '',
      current_pincode: '',
      same_as_current: false,
      permanent_address: '',
      permanent_city: '',
      permanent_state: '',
      permanent_pincode: '',
      ...(draft?.address || {}),
    },
    work: {
      department: '',
      designation: '',
      emergency_contact_name: '',
      emergency_contact_phone: '',
      requested_apps: [],
      ...(draft?.work || {}),
    },
    documents: {
      aadhar: null,
      pan: null,
      photo: null,
      confirmation: false,
    },
  });

  const updateFormData = (section, data) => {
    setFormData((prev) => ({
      ...prev,
      [section]: { ...prev[section], ...data },
    }));
  };

  // Keep the draft in sync as the user types. Documents are deliberately left
  // out (see DRAFT_KEY comment above) — only re-saving personal/address/work.
  useEffect(() => {
    try {
      sessionStorage.setItem(
        DRAFT_KEY,
        JSON.stringify({
          currentStep,
          personal: formData.personal,
          address: formData.address,
          work: formData.work,
        })
      );
    } catch {
      // sessionStorage can throw in private-browsing/quota-exceeded cases —
      // draft backup is a convenience, not a requirement, so fail silently
    }
  }, [currentStep, formData.personal, formData.address, formData.work]);

  // MSAL already has the signed-in identity by the time this component mounts —
  // company_email was being left blank because nothing ever read it from the
  // account and pushed it into formData. account.username is the UPN (email)
  // for Entra ID accounts; idTokenClaims.email is a fallback for tenants where
  // that claim is configured differently.
  useEffect(() => {
    const account = accounts[0];
    if (!account) return;
    const email = account.username || account.idTokenClaims?.email || '';
    if (email) {
      setFormData((prev) => ({
        ...prev,
        personal: { ...prev.personal, company_email: email },
      }));
    }
  }, [accounts]);

  const handleNext = () => {
    setCurrentStep((prev) => Math.min(prev + 1, 4));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // uploadedDocKeys comes from Step4Documents and already uses the backend
  // field names: { aadharDocKey, panDocKey, photoKey }
  const handleSubmit = async (uploadedDocKeys) => {
    setIsSubmitting(true);
    setSubmitError('');
    try {
      // IMPORTANT: The backend (onboarding_handler.py) expects camelCase keys
      // at the TOP LEVEL of the body — NOT nested and NOT snake_case.
      const payload = {
        // Personal
        fullName: formData.personal.full_name,
        personalEmail: formData.personal.personal_email || null,
        phone: formData.personal.phone,
        dateOfBirth: formData.personal.date_of_birth,
        gender: (formData.personal.gender || '').toUpperCase(),
        bloodGroup: formData.personal.blood_group || null,

        // Address
        currentAddress: formData.address.current_address,
        currentCity: formData.address.current_city,
        currentState: formData.address.current_state,
        currentPincode: formData.address.current_pincode,
        permanentAddress: formData.address.permanent_address,
        permanentCity: formData.address.permanent_city,
        permanentState: formData.address.permanent_state,
        permanentPincode: formData.address.permanent_pincode,

        // Work
        department: formData.work.department,
        designation: formData.work.designation,
        emergencyContactName: formData.work.emergency_contact_name,
        emergencyContactPhone: formData.work.emergency_contact_phone,
        requestedApps: formData.work.requested_apps,

        // Documents — top-level keys, exactly as backend REQUIRED_FIELDS expects
        aadharDocKey: uploadedDocKeys.aadharDocKey,
        panDocKey: uploadedDocKeys.panDocKey,
        photoKey: uploadedDocKeys.photoKey,

        // Confirmation checkbox — backend requires this to be boolean true
        confirmationAccepted: true,
      };

      await submitOnboarding(payload);
      try { sessionStorage.removeItem(DRAFT_KEY); } catch { /* non-critical */ }
      onComplete();
    } catch (err) {
      setSubmitError(err.message || 'Submission failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const steps = [
    { number: 1, label: 'Personal', icon: '01' },
    { number: 2, label: 'Address', icon: '02' },
    { number: 3, label: 'Work', icon: '03' },
    { number: 4, label: 'Documents', icon: '04' },
  ];

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step1Personal
            data={formData.personal}
            onChange={(data) => updateFormData('personal', data)}
            onNext={handleNext}
          />
        );
      case 2:
        return (
          <Step2Address
            data={formData.address}
            onChange={(data) => updateFormData('address', data)}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 3:
        return (
          <Step3Work
            data={formData.work}
            availableApps={availableApps}
            onChange={(data) => updateFormData('work', data)}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 4:
        return (
          <Step4Documents
            data={formData.documents}
            onChange={(data) => updateFormData('documents', data)}
            onSubmit={handleSubmit}
            onBack={handleBack}
            isSubmitting={isSubmitting}
          />
        );
      default:
        return null;
    }
  };

  // ============ WELCOME SCREEN ============
  if (!showWizard) {
    return (
      <div className="ob-page">
        <div className="ob-welcome">
          <div className="ob-welcome__badge">New Employee</div>
          <h1 className="ob-welcome__title">
            Welcome to <span>CloudTechOn</span>
          </h1>
          <p className="ob-welcome__desc">
            Complete your onboarding profile to get started. This process takes
            about 3-5 minutes and includes personal details, address, work
            information, and document verification.
          </p>

          <div className="ob-welcome__steps">
            <div className="ob-welcome__step">
              <div className="ob-welcome__step-num">1</div>
              <div>
                <strong>Personal Details</strong>
                <span>Name, email, phone, DOB</span>
              </div>
            </div>
            <div className="ob-welcome__step">
              <div className="ob-welcome__step-num">2</div>
              <div>
                <strong>Address</strong>
                <span>Current &amp; permanent address</span>
              </div>
            </div>
            <div className="ob-welcome__step">
              <div className="ob-welcome__step-num">3</div>
              <div>
                <strong>Work &amp; Access</strong>
                <span>Department, designation, apps</span>
              </div>
            </div>
            <div className="ob-welcome__step">
              <div className="ob-welcome__step-num">4</div>
              <div>
                <strong>Documents</strong>
                <span>Aadhar, PAN, photo upload</span>
              </div>
            </div>
          </div>

          <button className="ob-welcome__btn" onClick={() => setShowWizard(true)}>
            Get Started
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
    );
  }

  // ============ WIZARD ============
  return (
    <div className="ob-page">
      <div className="ob-wizard">
        {/* Sidebar Stepper */}
        <aside className="ob-sidebar">
          <div className="ob-sidebar__brand">
            <div className="ob-sidebar__logo">
              <img src={logo} alt="CloudTechOn Logo" />
            </div>
            <span>CloudTechOn</span>
          </div>
          <nav className="ob-sidebar__nav">
            {steps.map((step) => (
              <div
                key={step.number}
                className={`ob-sidebar__item ${
                  currentStep === step.number ? 'active' : ''
                } ${currentStep > step.number ? 'done' : ''}`}
              >
                <div className="ob-sidebar__dot">
                  {currentStep > step.number ? (
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M3 7l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ) : (
                    step.icon
                  )}
                </div>
                <span className="ob-sidebar__label">{step.label}</span>
              </div>
            ))}
          </nav>
          <div className="ob-sidebar__progress">
            <div className="ob-sidebar__progress-bar">
              <div
                className="ob-sidebar__progress-fill"
                style={{ height: `${((currentStep - 1) / 3) * 100}%` }}
              />
            </div>
          </div>
        </aside>

        {/* Form Area */}
        <div className="ob-main">
          {submitError && (
            <div className="ob-alert ob-alert--error">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M8 5v3M8 10h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              {submitError}
            </div>
          )}
          <div className="ob-form-area">{renderStep()}</div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;
